import React from 'react';
import { useTranslation } from 'react-i18next';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import { alpha } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';

import Iconify from '../../../theme/iconify/iconify';

import Subsection from './Subsection';
import { useResponsive } from '../../../hooks/use-responsive';

function Content({
    course,
    activeSection,
    setActiveSection,
    update,
    saveSection,
    editingSection,
    setEditingSection,
    setEditingSubsection,
    setEditSubsectionIndex,
    sectionTitle,
    setSectionTitle,
    sectionVisibility,
    setSubsectionTitle,
    setSubsectionContent,
    setQuiz,
    resetQuiz,
    setFindOutMorePrompt,
    setTOC,
    setQuizModal,
    setContentModal,
    setSubsectionModal,
    setRemoveQuizModal,
    setRemoveSectionModal,
    setRemoveSubsectionModal,
    user,
    updateUser
}) {
    const { t } = useTranslation();
    const mdUp = useResponsive('up', 'md');

    const moveSubsection = (data) => {
        if (!user) return;
        update(data);
    }

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            saveSection();
        }
    }

    return (
        <>
            {course.sections.length > 0 && (
                <>
                    <Stack direction='row' alignItems='center' sx={{ m: 1, mb: 2 }}>
                        <Grid item md={editingSection ? 11 : 12} xs={editingSection ? 10 : 12} sx={{ display: 'flex !important', alignItems: 'center' }}>
                            {editingSection ?
                                <input
                                    id='mISLec_edit_course_section_title'
                                    type='text'
                                    className='mISLec_edit_course_section_title'
                                    name='title'
                                    value={sectionTitle}
                                    onClick={(e) => e.preventDefault()}
                                    onChange={(e) => setSectionTitle(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    style={{
                                        color: '#637381 !important'
                                    }}
                                />
                                :
                                <Grid item xs={12} className='mISLec_course_heading' sx={{ pb: '0px !important' }}>
                                    <Grid container className='mISLec_course_heading' sx={{ flexDirection: { xs: 'column-reverse', md: 'row' } }}>
                                        <Grid item xs={12} md={(user.role === 'admin' || user._id === course.user) ? 8 : 12}>
                                            <Typography variant='h5'>
                                                {course.sections[activeSection]?.title}
                                            </Typography>
                                        </Grid>
                                        {(user.role === 'admin' || user._id === course.user) &&
                                            <Grid item xs={12} md={4} sx={{ display: 'flex', justifyContent: 'right' }}>
                                                <Tooltip title={t('course.tooltips.editSection')} arrow>
                                                    <IconButton
                                                        onClick={() => {
                                                            setSectionTitle(course.sections[activeSection].title);
                                                            setEditingSection(true);
                                                            setTimeout(() => {
                                                                document.getElementById('mISLec_edit_course_section_title').focus();
                                                            }, 100);
                                                        }}
                                                        sx={{
                                                            pt: 0,
                                                            pb: 0,
                                                            pr: '4px',
                                                            pl: '4px',
                                                            height: '28px',
                                                            borderRadius: '8px'
                                                        }}
                                                    >
                                                        <Iconify icon='eva:edit-outline' />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title={course.sections[activeSection]?.visible ? t('course.tooltips.hideSection') : t('course.tooltips.showSection')} arrow>
                                                    <IconButton
                                                        onClick={() => {
                                                            sectionVisibility();
                                                        }}
                                                        sx={{
                                                            pt: 0,
                                                            pb: 0,
                                                            pr: '5px',
                                                            pl: '5px',
                                                            height: '28px',
                                                            borderRadius: '8px'
                                                        }}
                                                    >
                                                        <Iconify icon={course.sections[activeSection]?.visible ? 'eva:eye-fill' : 'eva:eye-off-fill'} width={17} />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title={t('course.tooltips.removeSection')} arrow>
                                                    <IconButton
                                                        color='error'
                                                        onClick={() => {
                                                            setRemoveSectionModal(true);
                                                        }}
                                                        sx={{
                                                            pt: 0,
                                                            pb: 0,
                                                            pr: '5px',
                                                            pl: '5px',
                                                            height: '28px',
                                                            borderRadius: '8px'
                                                        }}
                                                    >
                                                        <Iconify icon='eva:trash-outline' width={17} />
                                                    </IconButton>
                                                </Tooltip>
                                            </Grid>
                                        }
                                    </Grid>
                                </Grid>
                            }
                        </Grid>
                        {editingSection ?
                            <Grid item md={editingSection ? 1 : 12} xs={editingSection ? 2 : 12}>
                                <Grid container>
                                    <Grid item xs={6} sx={{ display: 'flex', justifyContent: 'right' }}>
                                        <Tooltip title={t('course.tooltips.saveChanges')} arrow>
                                            <IconButton
                                                disableRipple
                                                disableFocusRipple
                                                onClick={(event) => {
                                                    event.preventDefault();
                                                    saveSection();
                                                }}
                                                sx={{ mr: 0, zIndex: 9, pr: 0, py: 0 }}>
                                                <Iconify icon='eva:checkmark-outline' />
                                            </IconButton>
                                        </Tooltip>
                                    </Grid>
                                    <Grid item xs={6} sx={{ display: 'flex', justifyContent: 'right' }}>
                                        <Tooltip title={t('course.tooltips.cancelChanges')} arrow>
                                            <IconButton
                                                disableRipple
                                                disableFocusRipple
                                                onClick={(event) => {
                                                    event.preventDefault();
                                                    setEditingSection(false);
                                                    setSectionTitle('');
                                                }}
                                                sx={{ mr: 0, zIndex: 9, pr: 0, py: 0, color: 'error.main' }}>
                                                <Iconify icon='eva:close-outline' />
                                            </IconButton>
                                        </Tooltip>
                                    </Grid>
                                </Grid>
                            </Grid>
                            : null}
                    </Stack>
                    <Stack direction='column' alignItems='center' justifyContent='space-between'>
                        {course.sections[activeSection]?.subsections.map((subsection, index) => (
                            <Subsection
                                key={index}
                                subsection={subsection}
                                index={index}
                                course={course}
                                activeSection={activeSection}
                                user={user}
                                updateUser={updateUser}
                                setFindOutMorePrompt={setFindOutMorePrompt}
                                moveSubsection={moveSubsection}
                                setEditingSubsection={setEditingSubsection}
                                setEditSubsectionIndex={setEditSubsectionIndex}
                                setRemoveSubsectionModal={setRemoveSubsectionModal}
                                setSubsectionModal={setSubsectionModal}
                                setSubsectionTitle={setSubsectionTitle}
                                setSubsectionContent={setSubsectionContent}
                                setQuiz={setQuiz}
                                resetQuiz={resetQuiz}
                                setQuizModal={setQuizModal}
                                setRemoveQuizModal={setRemoveQuizModal}
                            />
                        ))}
                    </Stack>
                </>
            )}
            <Stack direction='row' alignItems='center' justifyContent='center' mt={1} mb={course.sections[activeSection]?.subsections[0]?.quiz?.type !== 'exam' ? 2 : 1}>
                {(user.role === 'admin' || user._id === course.user) &&
                    <Button
                        variant=''
                        onClick={() => setContentModal(true)}
                        sx={{
                            borderRadius: '8px !important',
                            minWidth: {
                                xs: '100%',
                                md: 200
                            },
                            maxWidth: {
                                xs: '100%',
                                md: 200
                            },
                            backgroundColor: (theme) => ({
                                xs: alpha(theme.palette.primary.main, 0.08),
                                md: 'rgba(33, 43, 54, 0.08)'
                            }),
                            color: {
                                xs: 'primary.main',
                                md: 'inherit'
                            },
                            //flexDirection: 'column',
                            '&:hover': {
                                color: 'primary.main',
                                backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08)
                            }
                        }}
                    >
                        <Iconify icon='gridicons:add' sx={{ mr: 1, alignSelf: 'center' }} />
                        <Box component='span' sx={{ alignSelf: 'center' }}>{t('course.modals.content.title')}</Box>
                    </Button>
                }
            </Stack>
            <Stack direction='column' alignItems='center'>
                <Grid container>
                    <Grid item xs={6}>
                        {activeSection > 0 && (
                            <Button
                                variant=''
                                startIcon={<Iconify icon='mingcute:left-fill' width={24} />}
                                disabled={user.courses.find((c) => c.id === course._id)?.quiz?.some((q) => q.is_solving)}
                                onClick={() => {
                                    setActiveSection(activeSection - 1);
                                    setTOC(`${activeSection - 1}`);
                                    setTimeout(() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }, 100);
                                }}
                                sx={{
                                    borderRadius: '8px !important',
                                    color: 'primary.main',
                                    '&:hover': {
                                        backgroundColor: (theme) => alpha(theme.palette.primary.main, 0)
                                    }
                                }}
                            >
                                <Grid container>
                                    <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'left', textAlign: 'left', fontSize: '0.8rem' }}>
                                        <span style={{ display: 'block' }}>{t('util.pagination.previous')}</span>
                                    </Grid>
                                    {mdUp &&
                                        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'left', textAlign: 'left', fontSize: '0.95rem' }}>
                                            {course.sections[activeSection - 1].title?.toUpperCase()}
                                        </Grid>
                                    }
                                </Grid>
                            </Button>
                        )}
                    </Grid>
                    <Grid item xs={6} sx={{ display: 'flex', justifyContent: 'right' }}>
                        {activeSection < course.sections.length - 1 && (
                            <Button
                                endIcon={<Iconify icon='mingcute:right-fill' width={24} />}
                                disabled={user.courses.find((c) => c.id === course._id)?.quiz?.some((q) => q.is_solving)}
                                onClick={() => {
                                    setTOC(`${activeSection + 1}`);
                                    setActiveSection(activeSection + 1);
                                    setTimeout(() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }, 100);
                                }}
                                sx={{
                                    borderRadius: '8px !important',
                                    color: 'primary.main',
                                    '&:hover': {
                                        backgroundColor: (theme) => alpha(theme.palette.primary.main, 0)
                                    }
                                }}
                            >
                                <Grid container>
                                    <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'right', textAlign: 'right', fontSize: '0.8rem' }}>
                                        <span style={{ display: 'block' }}>{t('util.pagination.next')}</span>
                                    </Grid>
                                    {mdUp &&
                                        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'right', textAlign: 'right', fontSize: '0.95rem' }}>
                                            {course.sections[activeSection + 1].title?.toUpperCase()}
                                        </Grid>
                                    }
                                </Grid>
                            </Button>
                        )}
                    </Grid>
                </Grid>
            </Stack>
        </>
    );
}

export default Content;